/**
 * Which routes render inside `R5Shell` (and so carry their own language switch in the avatar menu). `InterfaceControls` in
 * `app/router.tsx` hides the legacy language strip on these paths. Add the auth, landing and tool routes here as their
 * r5 layouts replace the old chrome (PAGES.md 1.1 rule 7).
 */
const STUDENT_SHELL_PATHS = [
  '/home', '/dashboard', '/exams-library', '/my-exams', '/leaderboard', '/results', '/achievements', '/plan', '/profile', '/settings', '/keep-talking',
  // The school view (demo builds only; the string is harmless in production): it has its own shell and language switch.
  '/school',
] as const

/** The welcome page (`/start`) for the first full mock a teacher chose is a student shell page only in a build with the tasks flag; otherwise the path is a 404. */
const TASK_SHELL_PATHS = ['/start'] as const

export function isR5ShellPath(pathname: string): boolean {
  const paths = import.meta.env.VITE_ENABLE_TASKS === 'true' ? [...STUDENT_SHELL_PATHS, ...TASK_SHELL_PATHS] : STUDENT_SHELL_PATHS
  return paths.some((base) => pathname === base || pathname.startsWith(`${base}/`))
}

/** The two free public tools render inside `.r5` with their own language switch (features/calculators/ToolsFrame). */
const R5_TOOL_PATHS = ['/ielts-score-calculator', '/where-can-my-score-take-me'] as const

export function isR5ToolPath(pathname: string): boolean {
  return R5_TOOL_PATHS.some((base) => pathname === base)
}

/**
 * Public and auth routes that carry their own language switch (`R5AuthLayout`, `R5PublicLayout`), so `InterfaceControls`
 * hides the legacy strip there too. These do not run a route View Transition (only `isR5ShellPath` pages report a commit).
 * The 404 page is reached on any path; `r5-public.css` hides the strip for it with `:has`.
 */
const PUBLIC_PATHS = ['/', '/landing', '/login', '/register', '/forgot-password', '/reset-password', '/activate', '/admin/activate', '/admin/forgot-password', '/admin/reset-password'] as const

export function isR5PublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.includes(pathname as (typeof PUBLIC_PATHS)[number])
}


/**
 * Admin pages inside `AdminShell` (which reports route commits too, so its links may run a View Transition). Never the
 * runner preview (`/admin/tests/:id/preview`, the exam room) and never the public activation and password reset pages
 * (outside the shell).
 */
export function isAdminShellPath(pathname: string): boolean {
  if (!pathname.startsWith('/admin/')) return false
  if (/^\/admin\/tests\/[^/]+\/preview\/?$/i.test(pathname)) return false
  return !isR5PublicPath(pathname)
}
